import { formatNumber } from 'shared/utils';
import {
  FLOOD_CATEGORIES,
  FloodCategory,
  FloodCategoryLevel,
} from './waterLevel.types';

export type ShouldDisplayWaterLevelForecastArgs = {
  floodCategories: FloodCategoryLevel[];
  currentLevel: number | undefined;
  forecastPeakLevel: number | undefined;
};

export const formatWaterLevelValue = (level: number): string =>
  formatNumber(level, 1);

export const numericWaterLevelForDisplay = (level: number): number =>
  Number(formatWaterLevelValue(level).replace(/,/g, ''));

export const waterLevelsEqualForDisplay = (a: number, b: number): boolean =>
  numericWaterLevelForDisplay(a) === numericWaterLevelForDisplay(b);

export const getFloodCategoryFromLevel = (
  level: number,
  categoryLevels: FloodCategoryLevel[],
): FloodCategory | null => {
  const sortedCategoryLevelsDescending = [...categoryLevels].sort(
    (a, b) => b.level - a.level,
  );
  const category = sortedCategoryLevelsDescending.find(
    (fc) => level >= fc.level,
  );
  if (category) {
    return category.category;
  }
  // Below all thresholds but has flood categories defined = none/normal
  return categoryLevels.length > 0 ? 'none' : null;
};

/**
 * Whether to show forecast peak level in UI (details header, flood impacts list, etc.).
 * Aligns with map styling in `WaterLevelLayer.styles.ts`: `hasForecastCategoryFilter`
 * and the forecast branch of `displayForecastFilter` — no forecast when the gauge has
 * no flood stage definitions, when the peak stays in the `none` stage, when peak
 * equals current for display, or when there is no current observation level.
 */
export const shouldDisplayWaterLevelForecast = (
  args: ShouldDisplayWaterLevelForecastArgs,
): boolean => {
  const { floodCategories, currentLevel, forecastPeakLevel } = args;

  if (currentLevel === undefined || forecastPeakLevel === undefined) {
    return false;
  }

  if (floodCategories.length === 0) {
    return false;
  }

  if (waterLevelsEqualForDisplay(forecastPeakLevel, currentLevel)) {
    return false;
  }

  const peakCategory = getFloodCategoryFromLevel(
    forecastPeakLevel,
    floodCategories,
  );

  if (peakCategory === null || peakCategory === FLOOD_CATEGORIES.none) {
    return false;
  }

  return true;
};
